import { CARD_FONTS, CARD_HEIGHT, CARD_WIDTH, type CardData, drawShareCard } from './share-card';

export type ShareOutcome = 'shared' | 'copied' | 'failed';

/** Рисует карточку результата в PNG. null — если canvas недоступен (тогда делимся одним текстом). */
export async function renderShareCard(card: CardData): Promise<Blob | null> {
  try {
    // Шрифты подключены через @font-face и грузятся лениво — без этого canvas нарисует запасным шрифтом
    await Promise.all(CARD_FONTS.map((f) => document.fonts?.load(f, 'Телепат Аа0')));
    const canvas = document.createElement('canvas');
    canvas.width = CARD_WIDTH;
    canvas.height = CARD_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    drawShareCard(ctx, card);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  } catch {
    return null;
  }
}

/**
 * Делимся результатом: нативное окно (с картинкой, если платформа умеет файлы), иначе — копируем текст со ссылкой.
 * Картинку готовим заранее (`renderShareCard`): Safari требует, чтобы `navigator.share` вызывался сразу по клику.
 */
export async function shareResult(opts: {
  text: string;
  url: string;
  title: string;
  image: Blob | null;
}): Promise<ShareOutcome> {
  const full = opts.url ? `${opts.text}\n${opts.url}` : opts.text;
  try {
    if (navigator.share) {
      const file = opts.image ? new File([opts.image], 'telepathy.png', { type: 'image/png' }) : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        // ссылку кладём в текст: часть приложений теряет отдельное поле url, когда есть файл
        await navigator.share({ files: [file], title: opts.title, text: full });
      } else {
        await navigator.share({ title: opts.title, text: opts.text, ...(opts.url ? { url: opts.url } : {}) });
      }
      return 'shared';
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return 'shared'; // игрок закрыл окно шаринга
  }
  return copyText(full);
}

async function copyText(text: string): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      return document.execCommand('copy') ? 'copied' : 'failed';
    } catch {
      return 'failed';
    } finally {
      ta.remove();
    }
  }
}

/** Сохраняет картинку файлом — для десктопа, где нативного «Поделиться» с файлами нет. */
export function downloadImage(image: Blob, name = 'telepathy.png'): void {
  const url = URL.createObjectURL(image);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
